/**
 * The build system (R7–R12, R31, R32).
 *
 * Implements the production state machine: four independent queues per player
 * advancing concurrently, prerequisites, power-scaled progress, explicit
 * placement, and the deploy that founds a base.
 *
 * All queue state lives in flat typed arrays rather than object graphs, for the
 * same reason entity state does (KTD8): it hashes as a linear scan, and it
 * cannot acquire an accidental iteration-order dependency.
 *
 * `powerFactor` is what makes AE4's proportional degradation fall out of the
 * machine rather than needing a special case — progress accrues at
 * `nominalRate × powerFactor`, and nothing else has to know about power.
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import * as fix from '../core/fixed.js'
import { BUILD_RADIUS, MAP_SIZE, MAX_PLAYERS, MAX_QUEUE_DEPTH } from '../core/config.js'
import { World } from './world.js'
import { Entities, EF, ST, NO_ENTITY } from './entities.js'
import { Pathfind } from './pathfind.js'
import { Economy } from './economy.js'
import { CMD, REASON, type Command, type Reason } from '../net/commands.js'
import { ENTITY_DEFS, ROLE, TAB, defById, type EntityDef } from './data/entities.js'
import { SimBridge } from './bridge.js'

/**
 * What a sale returns, as a percentage of build cost, before health scaling.
 *
 * Half is the figure this genre settled on and it is a good one: enough that
 * repositioning a base is a real option, little enough that building and
 * selling is never a way to make money.
 */
const SELL_REFUND_PERCENT = 50

/** Full repair costs this share of the build cost. */
const REPAIR_COST_PERCENT = 50

/** Ticks to take a structure from nothing to full. Twenty seconds at 30Hz. */
const REPAIR_TICKS = 600

/** What each producer past the first adds to build speed, and how many count. */
const PRODUCER_BONUS = 0.35
const PRODUCER_BONUS_CAP = 3

const TABS = 4
const READY_SLOTS = 8
/**
 * Progress accumulates in **Q16.16 ticks**, not whole ticks.
 *
 * Truncating the per-tick increment to an integer looks harmless and is not: at
 * a power factor of 0.7 the increment rounds to zero and production stops dead
 * rather than slowing down, which is precisely the hard cutoff R10 exists to
 * avoid. An item completes when progress reaches `fromInt(buildTime)`.
 */
const PROGRESS_PER_TICK = fix.ONE

/** Ticks a refinery waits before dispatching a replacement harvester. */
const RELIEF_DELAY = 60 * 30

export class Build implements Subsystem {
  static id = 'build'
  static deps = ['world', 'entities', 'pathfind', 'economy'] as const

  private ctx!: Ctx
  private world!: World
  private entities!: Entities
  private path!: Pathfind
  private economy!: Economy

  /** `[player][tab][index]` flattened: kind at even, progress at odd. */
  private readonly queue = new Int32Array(MAX_PLAYERS * TABS * MAX_QUEUE_DEPTH * 2)
  private readonly queueLen = new Int32Array(MAX_PLAYERS * TABS)
  /** Structures finished and awaiting a footprint. */
  private readonly ready = new Int32Array(MAX_PLAYERS * READY_SLOTS)
  private readonly readyLen = new Int32Array(MAX_PLAYERS)
  /** Ticks a player has held a refinery and no harvester. */
  private readonly harvesterless = new Int32Array(MAX_PLAYERS)
  /** Superweapon charge in ticks, and the structure kind it belongs to. */
  readonly superCharge = new Int32Array(MAX_PLAYERS)
  readonly superKind = new Int32Array(MAX_PLAYERS)

  /** Match setting: everything completes the tick after it is queued. */
  private instantBuild = false

  init(ctx: Ctx): void {
    this.ctx = ctx
    this.world = ctx.get<World>('world')
    this.entities = ctx.get<Entities>('entities')
    this.path = ctx.get<Pathfind>('pathfind')
    this.economy = ctx.get<Economy>('economy')
    this.instantBuild = ctx.get<SimBridge>('sim').instantBuild

    ctx.hash.register('build:queue', this.queue)
    ctx.hash.register('build:queueLen', this.queueLen)
    ctx.hash.register('build:ready', this.ready)
    ctx.hash.register('build:readyLen', this.readyLen)
    ctx.hash.register('build:superCharge', this.superCharge)
    ctx.hash.register('build:superKind', this.superKind)
    ctx.hash.register('build:harvesterless', this.harvesterless)
    ctx.hash.register('build:rally', this.rally)
    ctx.hash.register('build:primary', this.primary)
  }

  // --- queue access --------------------------------------------------------

  private base(player: number, tab: number): number {
    return (player * TABS + tab) * MAX_QUEUE_DEPTH * 2
  }

  queueDepth(player: number, tab: number): number {
    return this.queueLen[player * TABS + tab]
  }

  queueKindAt(player: number, tab: number, index: number): number {
    return this.queue[this.base(player, tab) + index * 2]
  }

  /** Build progress of a queue entry, as Q16.16 in [0, 1]. */
  queueProgressAt(player: number, tab: number, index: number): fix.Fixed {
    const kind = this.queueKindAt(player, tab, index)
    const def = defById(kind)
    if (!def) return fix.ZERO
    const raw = this.queue[this.base(player, tab) + index * 2 + 1]
    return fix.div(fix.raw(raw), fix.fromInt(def.buildTime))
  }

  readyCount(player: number): number {
    return this.readyLen[player]
  }

  readyKindAt(player: number, index: number): number {
    return this.ready[player * READY_SLOTS + index]
  }

  isReadyToPlace(player: number, kind: number): boolean {
    for (let i = 0; i < this.readyLen[player]; i++) {
      if (this.ready[player * READY_SLOTS + i] === kind) return true
    }
    return false
  }

  // --- rules ---------------------------------------------------------------

  /** Does the player hold a live, finished structure of this kind? */
  hasStructure(player: number, kind: number): boolean {
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      if (this.entities.owner[s] !== player) continue
      if (this.entities.kind[s] !== kind) continue
      if (this.entities.has(s, EF.Building)) continue
      return true
    }
    return false
  }

  /**
   * Prerequisites met (R9), checked **transitively**.
   *
   * A Guardian Tank names only the Foundry, but the Foundry names the Refinery.
   * Destroying the Refinery therefore makes the tank unbuildable even though
   * the Foundry still stands — which is exactly what AE3 specifies, and what
   * makes a raid on the economy worth running. Losing a prerequisite removes
   * its dependents from the buildable set immediately and does **not** cancel
   * anything already in flight.
   *
   * The tech tree is a small DAG and the validator proves it acyclic, so plain
   * recursion terminates.
   */
  prereqsMet(player: number, def: EntityDef): boolean {
    for (const p of def.prereqs) {
      if (!this.hasStructure(player, p)) return false
      const prereqDef = defById(p)
      if (prereqDef && !this.prereqsMet(player, prereqDef)) return false
    }
    return true
  }

  /**
   * The producing structure for a tab, or NO_ENTITY.
   *
   * The player's chosen primary wins if it is still standing and still builds
   * this tab. Otherwise the first one found, which is the old behaviour and
   * the right fallback: a primary that has just been destroyed should not stop
   * production, it should quietly hand over to whatever is left.
   */
  producerFor(player: number, tab: number): number {
    const chosen = this.primary[player * TABS + tab]
    if (chosen !== NO_ENTITY) {
      const slot = this.entities.resolve(chosen)
      if (
        slot >= 0 &&
        this.entities.owner[slot] === player &&
        !this.entities.has(slot, EF.Building) &&
        defById(this.entities.kind[slot])?.producesTab === tab
      ) {
        return chosen
      }
    }
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      if (this.entities.owner[s] !== player) continue
      if (this.entities.has(s, EF.Building)) continue
      const def = defById(this.entities.kind[s])
      if (def?.producesTab === tab) return this.entities.idAt(s)
    }
    // Structures and defences are both placed from the base root.
    if (tab === TAB.Defense) return this.producerFor(player, TAB.Structure)
    return NO_ENTITY
  }

  canBuild(player: number, kind: number): boolean {
    const def = defById(kind)
    if (!def) return false
    if (!this.prereqsMet(player, def)) return false
    return this.producerFor(player, def.tab) !== NO_ENTITY
  }

  // --- commands ------------------------------------------------------------

  handleCommand(cmd: Command): boolean {
    switch (cmd.t) {
      case CMD.Queue:
        this.tryQueue(cmd.slot, cmd.tab, cmd.kind)
        return true
      case CMD.Cancel:
        this.cancel(cmd.slot, cmd.tab, cmd.index)
        return true
      case CMD.Place:
        this.tryPlace(cmd.slot, cmd.kind, cmd.cx, cmd.cy)
        return true
      case CMD.Deploy:
        this.tryDeploy(cmd.slot, cmd.unit)
        return true
      case CMD.Repair:
        this.toggleRepair(cmd.slot, cmd.structure)
        return true
      case CMD.Sell:
        this.trySell(cmd.slot, cmd.structure)
        return true
      case CMD.Rally:
        this.setRally(cmd.slot, cmd.structure, cmd.cx, cmd.cy)
        return true
      case CMD.Primary:
        this.setPrimary(cmd.slot, cmd.structure)
        return true
      case CMD.Load:
        this.tryLoad(cmd.slot, cmd.unit, cmd.transport)
        return true
      case CMD.Mend:
        this.tryMend(cmd.slot, cmd.unit, cmd.structure)
        return true
      default:
        return false
    }
  }

  private reject(player: number, reason: Reason, kind = 0, detail?: string): void {
    this.ctx.emit('build:rejected', { owner: player, reason, kind, detail })
    this.ctx.emit('order:rejected', { owner: player, reason, kind, detail })
  }

  /** Queue an item. Credits deduct immediately (R7). */
  tryQueue(player: number, tab: number, kind: number): boolean {
    const def = defById(kind)
    if (!def || def.tab !== tab) {
      this.reject(player, REASON.Malformed, kind)
      return false
    }
    if (this.producerFor(player, tab) === NO_ENTITY) {
      this.reject(player, REASON.NoProducer, kind)
      return false
    }
    if (!this.prereqsMet(player, def)) {
      this.reject(player, REASON.NoProducer, kind, 'prerequisites')
      return false
    }
    const lenIndex = player * TABS + tab
    if (this.queueLen[lenIndex] >= MAX_QUEUE_DEPTH) {
      this.reject(player, REASON.QueueFull, kind)
      return false
    }
    if (!this.economy.trySpend(player, def.cost)) {
      this.reject(player, REASON.InsufficientCredits, kind)
      return false
    }

    const at = this.base(player, tab) + this.queueLen[lenIndex] * 2
    this.queue[at] = kind
    this.queue[at + 1] = 0
    this.queueLen[lenIndex]++
    this.ctx.emit('build:queued', { owner: player, tab, kind })
    return true
  }

  /**
   * Cancel a queue entry. Full refund while queued, prorated while building —
   * the player gets back what has not yet been turned into progress.
   */
  cancel(player: number, tab: number, index: number): boolean {
    const lenIndex = player * TABS + tab
    const len = this.queueLen[lenIndex]
    if (index < 0 || index >= len) return false

    const at = this.base(player, tab) + index * 2
    const kind = this.queue[at]
    const def = defById(kind)
    if (!def) return false

    const progress = this.queue[at + 1]
    const doneFraction = fix.div(fix.raw(progress), fix.fromInt(def.buildTime))
    const refund = fix.toInt(fix.mul(fix.fromInt(def.cost), fix.sub(fix.ONE, doneFraction)))
    this.economy.addCredits(player, refund)

    this.removeAt(player, tab, index)
    this.ctx.emit('build:cancelled', { owner: player, tab, kind, refund })
    return true
  }

  private removeAt(player: number, tab: number, index: number): void {
    const lenIndex = player * TABS + tab
    const len = this.queueLen[lenIndex]
    const start = this.base(player, tab)
    for (let i = index; i < len - 1; i++) {
      this.queue[start + i * 2] = this.queue[start + (i + 1) * 2]
      this.queue[start + i * 2 + 1] = this.queue[start + (i + 1) * 2 + 1]
    }
    this.queue[start + (len - 1) * 2] = 0
    this.queue[start + (len - 1) * 2 + 1] = 0
    this.queueLen[lenIndex] = len - 1
  }

  /**
   * Place a finished structure (R11).
   *
   * Rejection is **explicit and named**. No nearest-legal-cell snapping: that
   * feels broken precisely because it silently disagrees with the player.
   */
  tryPlace(player: number, kind: number, cx: number, cy: number): boolean {
    if (!this.isReadyToPlace(player, kind)) {
      this.reject(player, REASON.NoProducer, kind, 'nothing of that kind is awaiting placement')
      return false
    }
    const def = defById(kind)
    if (!def) return false

    const failure = this.footprintFailure(player, def, cx, cy, true)
    if (failure) {
      this.reject(player, failure, kind)
      return false
    }

    const id = this.spawnStructure(player, def, cx, cy)
    if (id === NO_ENTITY) {
      this.reject(player, REASON.Occupied, kind)
      return false
    }
    this.removeReady(player, kind)
    return true
  }

  /**
   * Put an infantryman inside a transport.
   *
   * Adjacency is required rather than ordered: the input layer walks the
   * infantry to the transport first and re-issues this when it arrives. Doing
   * the walk here would mean the build subsystem owning a movement order,
   * which is the pathfinder's job and would need its own state to track.
   *
   * The passenger is destroyed and its *kind* remembered. It has no position,
   * no separate health and nothing to simulate while carried, so keeping a
   * live entity parked off the map would be a second thing to hold in step for
   * no benefit — and it makes the right thing happen when the transport dies,
   * which is that the passenger dies with it.
   */
  tryLoad(player: number, unitId: number, transportId: number): boolean {
    const unit = this.entities.resolve(unitId)
    const transport = this.entities.resolve(transportId)
    if (unit < 0 || transport < 0) return false
    if (this.entities.owner[unit] !== player || this.entities.owner[transport] !== player) {
      this.reject(player, REASON.NotOwned)
      return false
    }
    const carrier = defById(this.entities.kind[transport])
    if (!carrier?.carriesInfantry) return false
    if (this.entities.passenger[transport] !== 0) return false

    const rider = defById(this.entities.kind[unit])
    if (!rider || rider.isStructure || rider.tab !== TAB.Infantry) return false

    // Adjacent only. A transport that could scoop somebody up from across the
    // map would make the walk pointless and the order impossible to cancel.
    const dx = (this.entities.x[unit] >> 16) - (this.entities.x[transport] >> 16)
    const dy = (this.entities.y[unit] >> 16) - (this.entities.y[transport] >> 16)
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) return false

    this.entities.passenger[transport] = rider.id
    this.path.release(unit)
    this.entities.destroy(unitId)
    this.ctx.emit('transport:load', { transport: transportId, kind: rider.id, owner: player })
    return true
  }

  /**
   * An engineer repairs an adjacent friendly structure to full, and is spent.
   *
   * Instant and total, against the credit-metered repair toggle's slow burn —
   * the trade is the engineer itself: a 500-credit unit that had to walk
   * there, against repair that costs nothing to switch on but takes its time.
   * Consuming the engineer is what keeps one from being a portable fountain
   * of free structure health.
   */
  tryMend(player: number, unitId: number, structureId: number): boolean {
    const unit = this.entities.resolve(unitId)
    const structure = this.entities.resolve(structureId)
    if (unit < 0 || structure < 0) return false
    if (this.entities.owner[unit] !== player || this.entities.owner[structure] !== player) {
      this.reject(player, REASON.NotOwned)
      return false
    }
    if (!defById(this.entities.kind[unit])?.isEngineer) return false
    if (!this.entities.has(structure, EF.Structure)) return false
    // A structure still rising has nothing to mend; the build finishes it.
    if (this.entities.has(structure, EF.Building)) return false
    if (this.entities.hp[structure] >= this.entities.maxHp[structure]) return false

    // Adjacent to the footprint, not the centre — a 3×3 building's centre is
    // out of reach from every cell an engineer can stand in.
    const def = defById(this.entities.kind[structure])
    const sx = (this.entities.x[structure] >> 16) - ((def?.width ?? 1) >> 1)
    const sy = (this.entities.y[structure] >> 16) - ((def?.height ?? 1) >> 1)
    const ux = this.entities.x[unit] >> 16
    const uy = this.entities.y[unit] >> 16
    const dx = ux < sx ? sx - ux : ux - (sx + (def?.width ?? 1) - 1)
    const dy = uy < sy ? sy - uy : uy - (sy + (def?.height ?? 1) - 1)
    if (Math.max(dx, dy) > 1) return false

    this.entities.hp[structure] = this.entities.maxHp[structure]
    this.path.release(unit)
    this.entities.destroy(unitId)
    this.ctx.emit('struct:mended', { structure: structureId, owner: player })
    return true
  }

  /**
   * Put the passenger back on the ground beside the transport.
   *
   * If there is nowhere free it stays aboard rather than being deleted or
   * stacked on top of something — a player pressing unload against a wall
   * should get nothing, not lose the unit.
   */
  tryUnload(slot: number): boolean {
    const kind = this.entities.passenger[slot]
    if (kind === 0) return false
    const def = defById(kind)
    if (!def) return false
    const cx = this.entities.x[slot] >> 16
    const cy = this.entities.y[slot] >> 16
    const cell = this.path.nearestFreeCell(cy * MAP_SIZE + cx, 3)
    if (cell < 0) return false

    const owner = this.entities.owner[slot]
    const sim = this.ctx.get<SimBridge>('sim')
    const id = sim.spawnDef(kind, owner, cell % MAP_SIZE, (cell / MAP_SIZE) | 0)
    if (!id) return false
    this.entities.passenger[slot] = 0
    this.ctx.emit('transport:unload', { transport: this.entities.idAt(slot), kind, owner })
    return true
  }

  /**
   * Dig in, or pack up again.
   *
   * A dug-in squad cannot move and takes markedly less fire. That trade is the
   * whole point: infantry standing in the open is the cheapest thing on the
   * map to kill, and a player who wants to hold a ramp or a chokepoint has
   * otherwise no answer but a building, which costs power and cannot be put
   * anywhere useful.
   *
   * The order stops the unit first. Digging in mid-stride and then arriving
   * somewhere else would leave the sandbags in one cell and the squad in the
   * next.
   */
  toggleDugIn(slot: number): boolean {
    const dug = this.entities.has(slot, EF.DugIn)
    if (!dug) this.path.stop(slot)
    this.entities.setFlag(slot, EF.DugIn, !dug)
    this.ctx.emit('unit:dugin', { slot, dug: !dug })
    return true
  }

  /**
   * Deploy a founding unit into its structure (R11, R30).
   *
   * **Exempt from the build area**, and that exemption is load-bearing: at tick
   * 0 the player owns no structure to be near, so F1's opening move would be
   * rejected by the blanket rule. The same exemption is what makes remote
   * expansion reachable.
   */
  tryDeploy(player: number, unitId: number): boolean {
    const slot = this.entities.resolve(unitId)
    if (slot < 0 || this.entities.owner[slot] !== player) {
      this.reject(player, REASON.NotOwned)
      return false
    }
    const unitDef = defById(this.entities.kind[slot])
    // Same key, three meanings now, decided by what the unit is. A founder
    // deploys into a building, infantry digs in where it stands, and a loaded
    // transport puts its passenger down. All three are "do the thing this unit
    // does here", which is why they share a binding rather than competing for
    // three.
    if (unitDef?.carriesInfantry) return this.tryUnload(slot)
    if (unitDef?.canDigIn) return this.toggleDugIn(slot)
    if (!unitDef?.founderOf) {
      this.reject(player, REASON.Malformed, this.entities.kind[slot], 'not a founder')
      return false
    }
    const structDef = defById(unitDef.founderOf)
    if (!structDef) return false

    // The structure is centred on the unit's cell.
    const cell = this.path.unitCell[slot]
    if (cell < 0) {
      this.reject(player, REASON.Occupied, structDef.id)
      return false
    }
    const cx = (cell % MAP_SIZE) - (structDef.width >> 1)
    const cy = ((cell / MAP_SIZE) | 0) - (structDef.height >> 1)

    // The founder's own cell must not count against it, so it is released
    // first — and restored if the footprint turns out to be illegal.
    this.path.release(slot)
    const failure = this.footprintFailure(player, structDef, cx, cy, false)
    if (failure) {
      this.path.place(slot, cell)
      this.reject(player, failure, structDef.id)
      return false
    }

    const id = this.spawnStructure(player, structDef, cx, cy)
    if (id === NO_ENTITY) {
      this.path.place(slot, cell)
      this.reject(player, REASON.Occupied, structDef.id)
      return false
    }
    this.entities.destroy(unitId)
    return true
  }

  /**
   * The first constraint a footprint fails, or null. Returning *which*
   * constraint is what lets R33's rejection feedback name a reason rather than
   * saying "no".
   */
  footprintFailure(
    player: number,
    def: EntityDef,
    cx: number,
    cy: number,
    requireBuildArea: boolean,
  ): Reason | null {
    for (let y = cy; y < cy + def.height; y++) {
      for (let x = cx; x < cx + def.width; x++) {
        if (!this.world.inBounds(x, y)) return REASON.OutOfBounds
        if (this.world.isCliff(x, y)) return REASON.Cliff
        if (this.world.isWater(x, y)) return REASON.Water
        // A ramp is a one-cell passage between elevation levels, and a building
        // on one seals everything above it. The bot sweep found the extreme
        // version: an AI walled its own base in, and the enemy's 142 units
        // stood idle for twenty simulated minutes because the only route was
        // under a refinery.
        if (this.world.isRamp(x, y)) return REASON.Ramp
        if (!this.world.isPassable(x, y)) return REASON.Occupied
        if (this.world.occupantAt(x, y) !== NO_ENTITY) return REASON.Occupied
        if (this.path.cellUnit[y * MAP_SIZE + x] !== 0) return REASON.Occupied
      }
    }
    if (requireBuildArea) {
      const inArea = this.world.hasOccupantWithin(
        cx,
        cy,
        def.width,
        def.height,
        BUILD_RADIUS,
        (id) => {
          const s = this.entities.resolve(id)
          return s >= 0 && this.entities.owner[s] === player
        },
      )
      if (!inArea) return REASON.OutsideBuildArea
    }
    return null
  }

  private spawnStructure(player: number, def: EntityDef, cx: number, cy: number): number {
    const sim = this.ctx.get<SimBridge>('sim')
    const id = sim.spawnDef(def.id, player, cx, cy)
    if (id === NO_ENTITY) return NO_ENTITY

    if (def.role === ROLE.Superweapon) {
      this.superKind[player] = def.id
      this.superCharge[player] = 0
    }

    // A refinery arrives with a free harvester, which is what makes F1's
    // economy start without any explicit mining order.
    if (def.freeUnit) {
      const spawnCell = this.exitCell(cx, cy, def)
      if (spawnCell >= 0) {
        sim.spawnDef(def.freeUnit, player, spawnCell % MAP_SIZE, (spawnCell / MAP_SIZE) | 0)
      }
    }
    return id
  }

  private removeReady(player: number, kind: number): void {
    const start = player * READY_SLOTS
    const len = this.readyLen[player]
    for (let i = 0; i < len; i++) {
      if (this.ready[start + i] !== kind) continue
      for (let k = i; k < len - 1; k++) this.ready[start + k] = this.ready[start + k + 1]
      this.ready[start + len - 1] = 0
      this.readyLen[player] = len - 1
      return
    }
  }

  // --- per-tick ------------------------------------------------------------

  fixedUpdate(): void {
    this.countProducers()
    for (let player = 0; player < MAX_PLAYERS; player++) {
      const factor = this.economy.factorOf(player)
      for (let tab = 0; tab < TABS; tab++) this.advanceQueue(player, tab, factor)
      this.advanceSuperweapon(player, factor)
      this.relieveHarvesterless(player)
    }
    this.advanceRepairs()
  }

  // --- rally points --------------------------------------------------------

  /**
   * Where a player's next unit of this kind walks to once it is built.
   *
   * Stored per (player, production tab) rather than per building, because that
   * is how production already works here: `finishUnit` asks for *a* producer
   * for the tab and uses whichever it finds. Keying the rally to a specific
   * structure would put the point on a building that may not be the one that
   * builds the unit, and the unit would walk somewhere the player never
   * clicked.
   *
   * A cell of -1 means none, which is why this is not a plain zero-filled
   * array: cell 0 is the top-left corner of the map and a legal place to rally.
   */
  private readonly rally = new Int32Array(MAX_PLAYERS * TABS).fill(-1)
  /**
   * The structure a player has nominated to build each tab, or NO_ENTITY.
   *
   * Hashed like everything else: which building a unit comes out of decides
   * where it appears, so two clients disagreeing about the primary would put
   * the same unit in two different places.
   */
  private readonly primary = new Int32Array(MAX_PLAYERS * TABS).fill(NO_ENTITY)

  /** Whether this structure is the one its owner builds that tab from. */
  isPrimary(structure: number): boolean {
    const slot = this.entities.resolve(structure)
    if (slot < 0) return false
    const def = defById(this.entities.kind[slot])
    if (def?.producesTab === undefined) return false
    return this.primary[this.entities.owner[slot] * TABS + def.producesTab] === structure
  }

  /**
   * Nominate a structure as the one to build from.
   *
   * Only matters with more than one of a kind, which is exactly when it
   * matters a great deal: without it, production comes out of whichever
   * building the entity table happens to reach first, which from the player's
   * side looks arbitrary — and is, since it depends on the order things were
   * built and destroyed in.
   */
  private setPrimary(player: number, structure: number): boolean {
    const slot = this.entities.resolve(structure)
    if (slot < 0 || this.entities.owner[slot] !== player) {
      this.reject(player, REASON.NotOwned)
      return false
    }
    const def = defById(this.entities.kind[slot])
    if (def?.producesTab === undefined) {
      this.reject(player, REASON.NoProducer, this.entities.kind[slot])
      return false
    }
    this.primary[player * TABS + def.producesTab] = structure
    this.ctx.emit('primary:set', { owner: player, tab: def.producesTab, structure })
    return true
  }

  rallyFor(player: number, tab: number): number {
    return this.rally[player * TABS + tab] ?? -1
  }

  private setRally(player: number, structure: number, cx: number, cy: number): void {
    const slot = this.entities.resolve(structure)
    if (slot < 0) return this.reject(player, REASON.StaleEntity)
    if (this.entities.owner[slot] !== player) return this.reject(player, REASON.NotOwned)
    const def = defById(this.entities.kind[slot])
    if (!def || !def.isStructure) return this.reject(player, REASON.NotStructure)
    // `producesTab`, not `tab`. The first is what the building *makes*; the
    // second is only which sidebar page the building itself is bought from, so
    // keying on it filed every factory's rally point under "structures" and
    // nothing ever walked anywhere.
    if (def.producesTab === undefined) return this.reject(player, REASON.NoProducer)
    if (!this.world.inBounds(cx, cy)) return this.reject(player, REASON.OutOfBounds)

    this.rally[player * TABS + def.producesTab] = cy * MAP_SIZE + cx
    this.ctx.emit('rally:set', { owner: player, tab: def.producesTab, cx, cy })
  }

  // --- repair and sell -----------------------------------------------------

  /**
   * Turn repair on or off for one structure.
   *
   * A toggle, because repairing is something you leave running while a fight
   * happens somewhere else. It switches itself off on completion, on running
   * out of money, and when the structure dies, so nothing has to remember to.
   */
  private toggleRepair(player: number, id: number): void {
    const slot = this.entities.resolve(id)
    if (slot < 0) return this.reject(player, REASON.StaleEntity)
    if (this.entities.owner[slot] !== player) return this.reject(player, REASON.NotOwned)
    if (!this.entities.has(slot, EF.Structure)) return this.reject(player, REASON.NotStructure)
    if (this.entities.has(slot, EF.Building)) return this.reject(player, REASON.StillBuilding)

    if (this.entities.has(slot, EF.Repairing)) {
      this.entities.setFlag(slot, EF.Repairing, false)
      this.ctx.emit('repair:stopped', { owner: player, id })
      return
    }
    if (this.entities.hp[slot] >= this.entities.maxHp[slot]) {
      return this.reject(player, REASON.Undamaged)
    }
    this.entities.setFlag(slot, EF.Repairing, true)
    this.ctx.emit('repair:started', { owner: player, id })
  }

  /**
   * Sell a structure back for part of what it cost.
   *
   * The refund follows the structure's remaining health rather than its sticker
   * price. Otherwise a building about to be destroyed is worth exactly as much
   * as a pristine one, and the correct play in every losing fight is to sell
   * the whole base out from under the attacker.
   */
  private trySell(player: number, id: number): void {
    const slot = this.entities.resolve(id)
    if (slot < 0) return this.reject(player, REASON.StaleEntity)
    if (this.entities.owner[slot] !== player) return this.reject(player, REASON.NotOwned)
    if (!this.entities.has(slot, EF.Structure)) return this.reject(player, REASON.NotStructure)

    const def = defById(this.entities.kind[slot])
    const maxHp = this.entities.maxHp[slot]
    const health = maxHp > 0 ? Math.max(0, Math.min(1, this.entities.hp[slot] / maxHp)) : 0
    // Integer arithmetic throughout: this runs inside the simulation and a
    // rounding difference between two clients is a desync.
    const refund = def ? Math.floor((def.cost * SELL_REFUND_PERCENT * Math.round(health * 100)) / 10000) : 0

    this.ctx.emit('structure:sold', {
      owner: player,
      id,
      kind: this.entities.kind[slot],
      refund,
      x: this.entities.x[slot],
      y: this.entities.y[slot],
    })
    this.entities.destroy(id)
    if (refund > 0) this.economy.addCredits(player, refund)
  }

  /**
   * Heal everything currently under repair, and charge for it.
   *
   * Cost is charged per tick against the health actually restored, so stopping
   * halfway costs half. The alternative — charging up front — means a structure
   * destroyed mid-repair takes the whole fee with it, which reads as the game
   * having stolen the money.
   */
  private advanceRepairs(): void {
    const entities = this.entities
    const limit = entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!entities.isAlive(slot)) continue
      if (!entities.has(slot, EF.Repairing)) continue

      const owner = entities.owner[slot]
      const maxHp = entities.maxHp[slot]
      if (maxHp <= 0 || entities.hp[slot] >= maxHp) {
        entities.setFlag(slot, EF.Repairing, false)
        this.ctx.emit('repair:stopped', { owner, id: entities.idAt(slot) })
        continue
      }

      // How much health this tick would restore, and what that is worth.
      const step = Math.max(1, Math.ceil(maxHp / REPAIR_TICKS))
      const healed = Math.min(step, maxHp - entities.hp[slot])
      const def = defById(entities.kind[slot])
      const cost = def ? Math.ceil((def.cost * REPAIR_COST_PERCENT * healed) / (maxHp * 100)) : 0

      if (cost > 0 && !this.economy.trySpend(owner, cost)) {
        entities.setFlag(slot, EF.Repairing, false)
        this.reject(owner, REASON.InsufficientCredits)
        this.ctx.emit('repair:stopped', { owner, id: entities.idAt(slot) })
        continue
      }

      entities.hp[slot] += healed
      if (entities.hp[slot] >= maxHp) {
        entities.setFlag(slot, EF.Repairing, false)
        this.ctx.emit('repair:stopped', { owner, id: entities.idAt(slot) })
      }
    }
  }

  /**
   * A refinery with no harvester dispatches one, free, after a delay.
   *
   * Without it a player can reach a state they can never leave: no harvesters,
   * so no income; not enough credits for a harvester, so no way to get one; and
   * a base full of buildings, so not eliminated either. The bot sweep found
   * matches frozen exactly there for twenty simulated minutes, both sides with
   * zero harvesters and a quarter of a million credits of ore still on the map.
   *
   * The delay is what keeps it from being free economy: a minute without income
   * is a real setback, and killing harvesters is still worth doing. It is the
   * *permanence* that was wrong, not the punishment. A refinery already ships
   * with a harvester (F1) — this is the same rule, applied when the pairing is
   * broken rather than only when the refinery is built.
   */
  private relieveHarvesterless(player: number): void {
    let refinery = -1
    let harvesters = 0
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      if (this.entities.owner[s] !== player) continue
      if (this.entities.has(s, EF.Harvester)) {
        harvesters++
        continue
      }
      if (refinery >= 0 || this.entities.has(s, EF.Building)) continue
      const def = defById(this.entities.kind[s])
      if (def?.freeUnit !== undefined && def.role === ROLE.Refinery) refinery = s
    }

    if (harvesters > 0 || refinery < 0) {
      this.harvesterless[player] = 0
      return
    }
    if (++this.harvesterless[player] < RELIEF_DELAY) return
    this.harvesterless[player] = 0

    const def = defById(this.entities.kind[refinery])
    if (!def?.freeUnit) return
    const cell = this.world.cellAt(
      fix.raw(this.entities.x[refinery]),
      fix.raw(this.entities.y[refinery]),
    )
    if (cell < 0) return
    const cx = (cell % MAP_SIZE) - (def.width >> 1)
    const cy = ((cell / MAP_SIZE) | 0) - (def.height >> 1)
    const exit = this.exitCell(cx, cy, def)
    if (exit < 0) return
    this.ctx
      .get<SimBridge>('sim')
      .spawnDef(def.freeUnit, player, exit % MAP_SIZE, (exit / MAP_SIZE) | 0)
    this.ctx.emit('build:relief', { owner: player, kind: def.freeUnit })
  }

  /** Each tab's front item advances. Tabs advance concurrently (R8). */
  private advanceQueue(player: number, tab: number, factor: fix.Fixed): void {
    const lenIndex = player * TABS + tab
    if (this.queueLen[lenIndex] === 0) return

    const at = this.base(player, tab)
    const kind = this.queue[at]
    const def = defById(kind)
    if (!def) {
      this.removeAt(player, tab, 0)
      return
    }

    // Losing the sole producing structure cancels an in-flight item with a
    // full refund — there is nothing left to build it.
    if (this.producerFor(player, tab) === NO_ENTITY) {
      this.economy.addCredits(player, def.cost)
      this.removeAt(player, tab, 0)
      this.reject(player, REASON.NoProducer, kind, 'producer destroyed')
      return
    }

    // Only one structure may await placement per kind, so a finished item does
    // not silently vanish while another of the same kind is pending.
    if (def.isStructure && this.isReadyToPlace(player, kind)) return

    // Power producers are exempt from the power slowdown. Low power slowing
    // the one building that ends low power is a death spiral: the deeper the
    // deficit, the slower the way out, all the way to a factor of zero — with
    // the yard and every plant gone, the replacement would never finish at
    // all, and losing your power would be losing the game on the spot.
    const effective = def.power > 0 ? fix.ONE : factor
    this.queue[at + 1] += this.instantBuild
      ? fix.fromInt(def.buildTime)
      : fix.mul(fix.mul(PROGRESS_PER_TICK, effective), this.producerSpeed(player, tab))
    if (this.queue[at + 1] < fix.fromInt(def.buildTime)) return

    this.removeAt(player, tab, 0)
    if (def.isStructure) this.finishStructure(player, def)
    else this.finishUnit(player, def)
  }

  /**
   * How much faster a second and third factory make things.
   *
   * Building another War Factory used to buy nothing but redundancy: progress
   * was a flat rate per tab however many producers were standing. That makes
   * the obvious Red Alert play — put down another factory when the money is
   * there — strictly worse than spending the same credits on units, and quietly
   * removes a decision the genre is built on.
   *
   * Diminishing, and capped. Linear scaling would make five factories five
   * times the output and turn every match into a race to spam them; a cap at
   * double keeps the second and third worth building and the fifth not.
   */
  private producerSpeed(player: number, tab: number): fix.Fixed {
    const extra = Math.max(0, this.producers[player * TABS + tab] - 1)
    const bonus = Math.min(extra, PRODUCER_BONUS_CAP) * PRODUCER_BONUS
    return fix.fromFloat(1 + bonus)
  }

  /**
   * Producers per player per tab, counted once per tick.
   *
   * Recomputed in one pass rather than asked per queue: the naive form scans
   * every entity for every player and tab, which is sixty-four walks of the
   * whole roster every tick to answer a question that changes when a building
   * finishes or falls.
   */
  private countProducers(): void {
    this.producers.fill(0)
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      if (this.entities.has(slot, EF.Building)) continue
      if (this.entities.has(slot, EF.Offline)) continue
      const owner = this.entities.owner[slot]
      if (owner < 0 || owner >= MAX_PLAYERS) continue
      const def = defById(this.entities.kind[slot])
      if (def?.producesTab === undefined) continue
      this.producers[owner * TABS + def.producesTab]++
    }
    // Defences are placed from the base root, so they inherit its count.
    for (let player = 0; player < MAX_PLAYERS; player++) {
      this.producers[player * TABS + TAB.Defense] = this.producers[player * TABS + TAB.Structure]
    }
  }

  private readonly producers = new Int32Array(MAX_PLAYERS * TABS)

  private finishStructure(player: number, def: EntityDef): void {
    const start = player * READY_SLOTS
    if (this.readyLen[player] >= READY_SLOTS) {
      // Nowhere to hold it. Refund rather than dropping it silently.
      this.economy.addCredits(player, def.cost)
      this.reject(player, REASON.QueueFull, def.id, 'too many structures awaiting placement')
      return
    }
    this.ready[start + this.readyLen[player]] = def.id
    this.readyLen[player]++
    this.ctx.emit('build:ready-to-place', { owner: player, kind: def.id })
    this.ctx.emit('build:complete', { owner: player, tab: def.tab, kind: def.id })
  }

  private finishUnit(player: number, def: EntityDef): void {
    const producer = this.producerFor(player, def.tab)
    const pSlot = this.entities.resolve(producer)
    if (pSlot < 0) {
      this.economy.addCredits(player, def.cost)
      return
    }
    const pDef = defById(this.entities.kind[pSlot])
    const cell = this.world.cellAt(fix.raw(this.entities.x[pSlot]), fix.raw(this.entities.y[pSlot]))
    const cx = cell % MAP_SIZE
    const cy = (cell / MAP_SIZE) | 0
    const exit = this.exitCell(
      cx - ((pDef?.width ?? 1) >> 1),
      cy - ((pDef?.height ?? 1) >> 1),
      pDef ?? def,
    )
    if (exit < 0) {
      // Completely walled in. Hold the unit rather than losing it.
      this.economy.addCredits(player, def.cost)
      this.reject(player, REASON.Occupied, def.id, 'no exit cell')
      return
    }

    const sim = this.ctx.get<SimBridge>('sim')
    const id = sim.spawnDef(def.id, player, exit % MAP_SIZE, (exit / MAP_SIZE) | 0)
    this.ctx.emit('build:complete', { owner: player, tab: def.tab, kind: def.id })

    // Walk it to the rally point, if there is one. Snapped to the coarse
    // lattice like every other group order, so a stream of units leaving a
    // factory shares one flow field instead of computing one each.
    const rally = this.rallyFor(player, def.tab)
    if (id && rally >= 0) {
      const goal = this.path.coarseDestination(rally)
      if (goal >= 0) this.path.orderGroup([id], goal, ST.Moving)
    }
  }

  /** A free cell just outside a structure's footprint, or the nearest free one. */
  /**
   * Where a finished unit is put down.
   *
   * **Free is not the same as usable.** The first version took the first free
   * cell it found around the footprint, and a free cell can be a pocket — a
   * gap between two buildings, or a notch against a cliff — with nowhere to go
   * from it. A unit placed there is built, paid for, and stuck, and the player
   * has no way to tell that from a pathfinding bug.
   *
   * So every candidate is scored by the size of the connected region it opens
   * into, then by how much open ground it touches. The region size is the real
   * answer to "can leave": a pocket between two buildings may have several
   * free neighbours and still be sealed, and units placed there are built,
   * paid for, and stuck. Open-neighbour count used to be the whole score and
   * that is exactly the failure it produced. It degrades gracefully: in a
   * tightly walled base every candidate shares the same small region and the
   * roomiest cell still wins.
   *
   * Ties go to the earliest candidate in a fixed order, which is what keeps
   * this deterministic — two clients scoring the same cells must choose the
   * same one.
   */
  private exitCell(cx: number, cy: number, def: EntityDef): number {
    let best = -1
    let bestRegion = -1
    let bestRoom = -1

    const consider = (x: number, y: number): void => {
      if (!this.path.isCellFree(x, y)) return
      const region = this.path.regionSizeAt(y * MAP_SIZE + x)
      const room = this.openNeighbours(x, y)
      if (region < bestRegion || (region === bestRegion && room <= bestRoom)) return
      bestRegion = region
      bestRoom = room
      best = y * MAP_SIZE + x
    }

    // The doorway sides first — below and above the footprint, then the flanks
    // — so that with everything else equal a unit still comes out of the front.
    for (let x = cx - 1; x <= cx + def.width; x++) {
      for (const y of [cy + def.height, cy - 1]) consider(x, y)
    }
    for (let y = cy; y < cy + def.height; y++) {
      for (const x of [cx + def.width, cx - 1]) consider(x, y)
    }
    if (best >= 0) return best

    return this.path.nearestFreeCell(
      Math.max(0, Math.min(MAP_SIZE - 1, cy)) * MAP_SIZE + Math.max(0, Math.min(MAP_SIZE - 1, cx)),
      6,
    )
  }

  /** Free cells touching this one, counting diagonals. Zero means a pocket. */
  private openNeighbours(cx: number, cy: number): number {
    let n = 0
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue
        if (this.path.isCellFree(cx + dx, cy + dy)) n++
      }
    }
    return n
  }

  // --- superweapon (R31) ---------------------------------------------------

  private advanceSuperweapon(player: number, factor: fix.Fixed): void {
    const kind = this.superKind[player]
    if (kind === 0) return
    const def = defById(kind)
    if (!def?.chargeTime) return

    // Losing the structure halts charging. Rebuilding resumes from zero,
    // because the charge belongs to the structure, not to the player.
    if (!this.hasStructure(player, kind)) {
      this.superCharge[player] = 0
      return
    }
    // Losing power halts charging **without** losing accumulated charge.
    if (factor < fix.ONE) return

    if (this.superCharge[player] < def.chargeTime) {
      this.superCharge[player]++
      if (this.superCharge[player] === def.chargeTime) {
        this.ctx.emit('super:ready', { owner: player, kind })
      }
    }
  }

  isSuperweaponReady(player: number): boolean {
    const kind = this.superKind[player]
    const def = defById(kind)
    if (!def?.chargeTime) return false
    return this.superCharge[player] >= def.chargeTime && this.hasStructure(player, kind)
  }

  /** Q16.16 charge fraction, for the HUD indicator. */
  superweaponProgress(player: number): fix.Fixed {
    const def = defById(this.superKind[player])
    if (!def?.chargeTime) return fix.ZERO
    return fix.div(fix.fromInt(this.superCharge[player]), fix.fromInt(def.chargeTime))
  }

  consumeSuperweaponCharge(player: number): void {
    this.superCharge[player] = 0
  }

  /** Kinds the player could queue right now, for the sidebar and the AI. */
  buildableKinds(player: number, tab: number): number[] {
    const out: number[] = []
    for (const def of ENTITY_DEFS) {
      if (def.tab !== tab) continue
      if (!this.ownsFaction(player, def)) continue
      if (!this.canBuild(player, def.id)) continue
      out.push(def.id)
    }
    return out
  }

  private ownsFaction(player: number, def: EntityDef): boolean {
    const limit = this.entities.limit
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s) || this.entities.owner[s] !== player) continue
      const own = defById(this.entities.kind[s])
      if (own && own.faction === def.faction) return true
    }
    return false
  }
}
